import React, { useEffect, useState } from "react";
import styled from "styled-components";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

// components
import { Alert, Button, Drawer, Form, Input } from "antd";

// utils
import { useAxios, useWindowSize } from "../../hooks";

const StyledForm = styled(Form)`
  .ant-form-item .ant-form-item-label > label::after {
    content: "";
  }
  .ant-col {
    margin-bottom: 5px;
  }

  @media (max-width: 768px) {
    .ant-form-item-label {
      padding: 0px;
    }
    .ant-form-item {
      margin-bottom: 10px !important;
    }
    .ant-btn {
      margin: 8px 0px 10px 0px;
    }
  }
`;

const DrawerFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: right;
`;

const CancelButton = styled(Button)`
  margin-right: 10px;
`;

const StyledAlert = styled(Alert)`
  margin-bottom: 20px;
`;

const EmailTemplateDrawer = ({ onboarding, open, onClose, record }) => {
  const [form] = Form.useForm();
  const { width } = useWindowSize();
  const { fetch: onSubmit, loading } = useAxios();
  const [error, setError] = useState({ type: null, message: null });
  const [content, setContent] = useState(null);
  const [signature, setSignature] = useState(null);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({ ...record });
      setContent(record?.message || "");
      setSignature(record?.signature || "");
    }
  }, [open]);

  const onBeforeClose = (value) => {
    setError({ type: null, message: null });
    form.resetFields();
    setContent(null);
    setSignature(null);
    onClose(value);
  };

  const onFinish = async (values) => {
    setError({ type: null, message: null });
    try {
      await onSubmit({
        method: "PUT",
        url: `/email-templates/onboarding/${onboarding.id}/${record.id}/`,
        data: { ...values, message: content, signature },
      });
      setError({ type: "success", message: "Template updated successfully." });
      onBeforeClose(true);
    } catch (err) {
      setError({ type: "error", message: err?.data?.message || err.message });
    }
  };

  return (
    <Drawer
      title="Edit Email Template"
      open={open}
      onClose={onBeforeClose}
      width={width < 768 ? width : 600}
      destroyOnHidden={true}
      footer={
        <DrawerFooter>
          <CancelButton
            block
            onClick={() => onBeforeClose()}
            disabled={loading}
          >
            Cancel
          </CancelButton>
          <Button
            loading={loading}
            type="primary"
            block
            onClick={() => form.submit()}
          >
            Save
          </Button>
        </DrawerFooter>
      }
    >
      {error?.message && (
        <StyledAlert type={error.type} message={error?.message} />
      )}
      <StyledForm form={form} onFinish={onFinish} layout="vertical">
        <Form.Item
          name="subject"
          label="Email Subject"
          rules={[
            {
              required: true,
              message: "This field is required",
            },
          ]}
        >
          <Input placeholder="Enter EMail Subject" />
        </Form.Item>
        <Form.Item
          label="Email Content"
          rules={[
            {
              required: true,
              message: "This field is required",
            },
          ]}
        >
          <ReactQuill
            bounds={10}
            theme="snow"
            value={content}
            onChange={setContent}
          />
        </Form.Item>
        <Form.Item
          name="link_text"
          label="Button Link Text"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input placeholder="Enter Link Button Text" />
        </Form.Item>
        <Form.Item
          name="footer_text"
          label="Footer Text"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input placeholder="Enter Footer Text" />
        </Form.Item>
        <Form.Item label="Signature">
          <ReactQuill
            bounds={10}
            theme="snow"
            value={signature}
            onChange={setSignature}
          />
        </Form.Item>
      </StyledForm>
    </Drawer>
  );
};

export default EmailTemplateDrawer;
